'use client'
import { ButtonIconTransparent } from '@fukku/button/Icon/Transparent'
import type { DialogAPI } from 'fukku/Dialog/Dialog'
import { IconPlusSmall } from 'icons/components/IconPlusSmall'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useProductCardContext } from 'product-card/hooks/useProductCardContext'
import { useProductColors } from 'product-card/hooks/useProductColors'
import { SizesBottomSheet } from 'product-components/components/SizeSelector/SizesBottomSheet/SizesBottomSheet'
import type { Product } from 'product/types'
import { useCallback, useMemo, useRef } from 'react'

import fukkuStyles from 'fukku/styles/classes.module.scss'
import type { PlpDomainData } from 'types/masterData/domainData/domainData'
import { useAddSelectedSize } from '../../../../hooks/useAddSelectedSize/useAddSelectedSize'
import { appsAddToBag } from '../../../../utils/appsAddToBag/appsAddToBag'
import styles from './SizeSheetIcon.module.scss'

interface AddToBagButtonProps {
	readonly product: Product
}

export const SizeSheetIcon = ({ product }: AddToBagButtonProps) => {
	const { isApp, custom } = useMasterData()
	const sizeSheetRef = useRef<DialogAPI>(null)
	const { t } = useLabels()

	const { productId, selectedColorId, lookId, location } =
		useProductCardContext()
	const { productColors } = useProductColors({ product })

	const productColorInfo = useMemo(
		() =>
			productColors?.find(
				(productColor) => productColor.id === selectedColorId
			),
		[productColors, selectedColorId]
	)

	const hasSizes = productColorInfo && productColorInfo.sizes.length > 1

	const onOpen = useCallback(() => {
		if (isApp) {
			appsAddToBag(productId, selectedColorId, lookId, custom as PlpDomainData)
		} else {
			sizeSheetRef.current?.show()
		}
	}, [isApp, productId, selectedColorId, lookId, custom])

	const { onAdd } = useAddSelectedSize({
		product,
		hasSizes,
		opened: sizeSheetRef.current?.open ?? false,
	})

	return (
		<>
			<ButtonIconTransparent
				className={styles.button}
				onClick={
					hasSizes
						? onOpen
						: () =>
								productColorInfo?.sizes[0] &&
								onAdd({
									size: {
										...productColorInfo?.sizes[0],
										isDelayed: false,
										available: true,
									},
									color: selectedColorId,
								})
				}
				data-testid='productCard.sizeSheetIconButton'
			>
				<IconPlusSmall className={styles.icon} />
				<span
					className={fukkuStyles.srOnly}
					data-testid={
						hasSizes
							? 'productCard.sizeSheetIconButton.multiSize'
							: 'productCard.sizeSheetIconButton.oneSize'
					}
				>
					{t('productCard.showSizes.button')}
				</span>
			</ButtonIconTransparent>
			{hasSizes && (
				<SizesBottomSheet
					ref={sizeSheetRef}
					product={product}
					productId={productId}
					selectedColorId={selectedColorId}
					onSelectedSize={onAdd}
					location={location}
				/>
			)}
		</>
	)
}
